if (!customElements.get("native-product-customization")) {
  customElements.define(
    "native-product-customization",
    class NativeProductCustomization extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.section = this.closest("section");
        this.selectedVariantId = String(this.dataset.selectedVariantId || "");
        this.mattressLimit = Number(this.dataset.mattressLimit || 0);
        this.bindAccordions();
        this.addEventListener("change", (event) => this.onAddonChange(event));
        this.addEventListener("input", (event) => this.onAddonInput(event));
        this.addEventListener("click", (event) => this.onAddonClick(event));
        this.section?.addEventListener("input", (event) => {
          if (event.target.matches('form[data-type="add-to-cart-form"] input[name="quantity"]')) this.updateOrderTotal();
        });
        this.section?.addEventListener("change", (event) => {
          if (event.target.matches('form[data-type="add-to-cart-form"] input[name="quantity"]')) this.updateOrderTotal();
        });
        this.section?.addEventListener("variantChangeEnd", (event) => this.onVariantChange(event));
        this.section?.addEventListener("variantProductInfoRendered", (event) => {
          const variantId = event.detail?.variant?.id;
          if (variantId) this.updateForVariant(String(variantId), false);
        });
        this.updateForVariant(this.selectedVariantId, false);
      }

      bindAccordions() {
        this.querySelectorAll(".native-product-customization__accordion-button").forEach((button) => {
          button.addEventListener("click", () => {
            const expanded = button.getAttribute("aria-expanded") === "true";
            const panel = this.querySelector(`#${CSS.escape(button.getAttribute("aria-controls"))}`);
            button.setAttribute("aria-expanded", String(!expanded));
            button.lastElementChild.textContent = expanded ? "+" : "−";
            if (panel) panel.hidden = expanded;
          });
        });
      }

      onVariantChange(event) {
        const picker = event.target.closest("variant-options") || event.target;
        const variantId = picker.currentVariant?.id || picker.dataset.variantId;
        if (variantId) this.updateForVariant(String(variantId), true);
      }

      updateForVariant(variantId, announce = true) {
        this.selectedVariantId = String(variantId);
        this.dataset.selectedVariantId = this.selectedVariantId;

        this.querySelectorAll("[data-native-availability-for]").forEach((item) => {
          item.hidden = item.dataset.nativeAvailabilityFor !== this.selectedVariantId;
        });

        const previouslySelected = new Map();
        this.querySelectorAll('[data-native-mattresses-for]:not([hidden]) [data-addon-select]:checked').forEach((input) => {
          previouslySelected.set(String(input.value), this.quantityFor(input.closest("[data-addon-card]")));
        });
        const previouslySelectedAccessories = new Map();
        this.querySelectorAll('[data-native-accessories-for]:not([hidden]) [data-addon-select]:checked').forEach((input) => {
          const card = input.closest("[data-addon-card]");
          previouslySelectedAccessories.set(this.addonVariantId(card), this.quantityFor(card));
        });
        const previouslySelectedVariantAddons = new Map();
        this.querySelectorAll('[data-native-addon-group-for]:not([hidden]) [data-addon-select]:checked').forEach((input) => {
          const card = input.closest("[data-addon-card]");
          const key = `${card?.dataset.addonCategory || ""}:${this.addonVariantId(card)}`;
          previouslySelectedVariantAddons.set(key, this.quantityFor(card));
        });

        let visibleGroup = null;
        this.querySelectorAll("[data-native-mattresses-for]").forEach((group) => {
          const show = group.dataset.nativeMattressesFor === this.selectedVariantId && Boolean(group.querySelector("[data-addon-card]"));
          group.hidden = !show;
          if (show) visibleGroup = group;
        });

        if (visibleGroup) {
          visibleGroup.querySelectorAll("[data-addon-select]").forEach((input) => {
            const savedQuantity = previouslySelected.get(String(input.value));
            input.checked = savedQuantity !== undefined;
            if (savedQuantity !== undefined) {
              const quantity = input.closest("[data-addon-card]")?.querySelector("[data-addon-quantity]");
              if (quantity) quantity.value = savedQuantity;
            }
          });
        }

        let visibleAccessoryGroup = null;
        this.querySelectorAll("[data-native-accessories-for]").forEach((group) => {
          const show = group.dataset.nativeAccessoriesFor === this.selectedVariantId && Boolean(group.querySelector("[data-addon-card]"));
          group.hidden = !show;
          if (show) visibleAccessoryGroup = group;
        });

        if (visibleAccessoryGroup) {
          visibleAccessoryGroup.querySelectorAll("[data-addon-card]").forEach((card) => {
            const input = card.querySelector("[data-addon-select]");
            const savedQuantity = previouslySelectedAccessories.get(this.addonVariantId(card));
            input.checked = savedQuantity !== undefined;
            if (savedQuantity !== undefined) {
              const quantity = card.querySelector("[data-addon-quantity]");
              if (quantity) quantity.value = savedQuantity;
            }
          });
        }

        let visibleVariantAddonCount = 0;
        this.querySelectorAll("[data-native-addon-group-for]").forEach((group) => {
          const show = group.dataset.nativeAddonGroupFor === this.selectedVariantId && Boolean(group.querySelector("[data-addon-card]"));
          group.hidden = !show;
          if (!show) return;

          visibleVariantAddonCount += group.querySelectorAll("[data-addon-card]").length;
          group.querySelectorAll("[data-addon-card]").forEach((card) => {
            const input = card.querySelector("[data-addon-select]");
            const key = `${card.dataset.addonCategory || ""}:${this.addonVariantId(card)}`;
            const savedQuantity = previouslySelectedVariantAddons.get(key);
            input.checked = savedQuantity !== undefined;
          });
          if (!group.querySelector("[data-addon-select]:checked")) {
            const noneInput = group.querySelector('[data-addon-card][data-addon-none="true"] [data-addon-select]');
            if (noneInput) noneInput.checked = true;
          }
          this.updateOptionalAddonSummary(group);
        });

        const availability = this.querySelector(`[data-native-availability-for="${CSS.escape(this.selectedVariantId)}"]`);
        const availabilityContainer = this.querySelector("[data-native-availability]");
        if (availabilityContainer) availabilityContainer.hidden = !availability?.textContent?.trim();
        const leadDays = availability?.dataset.leadDays;
        const status = availability?.dataset.availabilityStatus || "";
        const hasAvailabilityNote = availability?.dataset.hasAvailabilityNote === "true";
        const extended = leadDays !== undefined && leadDays !== "" && Number(leadDays) > 2;
        const unavailable = status === "unavailable";
        const deliveryBanner = this.section?.querySelector("[data-product-delivery-from]");
        if (deliveryBanner) deliveryBanner.hidden = hasAvailabilityNote || extended || unavailable;

        const submitButton = this.section?.querySelector('[id^="ProductSubmitButton-"]');
        if (submitButton && unavailable) {
          submitButton.disabled = true;
          submitButton.setAttribute("aria-disabled", "true");
        }

        this.updateOrderTotal();

        this.dispatchEvent(new CustomEvent("nativeAddonVariantUpdated", {
          bubbles: true,
          detail: { variantId: this.selectedVariantId, leadDays: leadDays === "" ? null : Number(leadDays), status, hasAvailabilityNote }
        }));

        if (announce) {
          const mattressCount = visibleGroup?.querySelectorAll("[data-addon-card]").length || 0;
          const accessoryCount = visibleAccessoryGroup?.querySelectorAll("[data-addon-card]").length || 0;
          const statusNode = this.querySelector("[data-native-addon-status]");
          statusNode.textContent = `Product extras updated. ${mattressCount} compatible mattress option${mattressCount === 1 ? "" : "s"}, ${accessoryCount} underbed accessor${accessoryCount === 1 ? "y" : "ies"}, and ${visibleVariantAddonCount} divan option${visibleVariantAddonCount === 1 ? "" : "s"} available.`;
        }
      }

      onAddonChange(event) {
        if (!event.target.matches("[data-addon-select], [data-addon-quantity], [data-addon-variant]")) return;

        if (event.target.matches("[data-addon-select]")) {
          this.enforceMattressLimit(event.target);
          this.enforceSingleCategorySelection(event.target);
          this.updateOptionalAddonSummary(event.target.closest("[data-native-addon-group-for]"));
        }

        this.updateOrderTotal();
      }

      onAddonInput(event) {
        if (event.target.matches("[data-addon-quantity], [data-addon-variant]")) this.updateOrderTotal();
      }

      onAddonClick(event) {
        this.onQuantityButtonClick(event);

        const choice = event.target.closest(".native-product-customization__choice");
        if (!choice || event.target.closest("select, button, input")) return;

        // A label toggles its checkbox after the click has propagated. Recalculate
        // on the next task so the visible Add control always updates the total.
        setTimeout(() => {
          const input = choice.querySelector("[data-addon-select]");
          if (input) {
            this.enforceMattressLimit(input);
            this.enforceSingleCategorySelection(input);
            this.updateOptionalAddonSummary(input.closest("[data-native-addon-group-for]"));
          }
          this.updateOrderTotal();
        }, 0);
      }

      enforceMattressLimit(input) {
        const card = input.closest("[data-addon-card]");
        if (card?.dataset.addonCategory !== "mattress" || !input.checked || this.mattressLimit !== 1) return;
        this.querySelectorAll('[data-addon-category="mattress"] [data-addon-select]:checked').forEach((selectedInput) => {
          if (selectedInput !== input) selectedInput.checked = false;
        });
      }

      enforceSingleCategorySelection(input) {
        const card = input.closest("[data-addon-card]");
        if (card?.dataset.addonSingleSelect !== "true" || !input.checked) return;
        const category = card.dataset.addonCategory;
        this.querySelectorAll(`[data-addon-card][data-addon-category="${CSS.escape(category)}"] [data-addon-select]:checked`).forEach((selectedInput) => {
          if (selectedInput !== input) selectedInput.checked = false;
        });
      }

      updateOptionalAddonSummary(group) {
        const summary = group?.querySelector("[data-addon-none-summary]");
        if (!summary) return;
        const selectedAddon = [...group.querySelectorAll("[data-addon-card]:not([data-addon-none='true'])")]
          .some((card) => card.querySelector("[data-addon-select]")?.checked);
        summary.textContent = selectedAddon ? " — Drawers selected" : " — Required";
      }

      onQuantityButtonClick(event) {
        const decrease = event.target.closest("[data-addon-quantity-decrease]");
        const increase = event.target.closest("[data-addon-quantity-increase]");
        if (!decrease && !increase) return;
        const input = event.target.closest("[data-addon-card]")?.querySelector("[data-addon-quantity]");
        if (!input) return;
        const minimum = Math.max(1, Number.parseInt(input.min || "1", 10) || 1);
        const current = Math.max(minimum, Number.parseInt(input.value || String(minimum), 10) || minimum);
        input.value = String(increase ? current + 1 : Math.max(minimum, current - 1));
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }

      quantityFor(card) {
        return Math.max(1, Number.parseInt(card?.querySelector("[data-addon-quantity]")?.value || "1", 10) || 1);
      }

      addonVariantId(card) {
        return String(card?.querySelector("[data-addon-variant]")?.value || card?.querySelector("[data-addon-select]")?.value || "");
      }

      mainQuantity() {
        const quantity = this.section?.querySelector('form[data-type="add-to-cart-form"] input[name="quantity"]');
        return Math.max(1, Number.parseInt(quantity?.value || "1", 10) || 1);
      }

      addonPrice(card) {
        const selector = card?.querySelector("[data-addon-variant]");
        const selectedOptionPrice = selector?.selectedOptions?.[0]?.dataset.price;
        return Math.max(0, Number.parseInt(selectedOptionPrice || card?.dataset.addonPrice || "0", 10) || 0);
      }

      formatMoney(cents) {
        const currency = window.Shopify?.currency?.active || "GBP";
        try {
          return new Intl.NumberFormat(document.documentElement.lang || "en-GB", {
            style: "currency",
            currency
          }).format(cents / 100);
        } catch (_error) {
          return `£${(cents / 100).toFixed(2)}`;
        }
      }

      updateOrderTotal() {
        const availability = this.querySelector(`[data-native-availability-for="${CSS.escape(this.selectedVariantId)}"]`);
        const mainPrice = Math.max(0, Number.parseInt(availability?.dataset.mainPrice || "0", 10) || 0);
        let total = mainPrice * this.mainQuantity();

        this.querySelectorAll("[data-addon-card]").forEach((card) => {
          if (card.closest("[data-native-mattresses-for]")?.hidden) return;
          if (card.closest("[data-native-accessories-for]")?.hidden) return;
          if (card.closest("[data-native-addon-group-for]")?.hidden) return;
          if (!card.querySelector("[data-addon-select]")?.checked) return;
          total += this.addonPrice(card) * this.quantityFor(card);
        });

        const value = this.querySelector("[data-native-order-total-value]");
        if (value) value.textContent = this.formatMoney(total);
      }

      selectedItems(groupId) {
        return [...this.querySelectorAll("[data-addon-card]")]
          .filter((card) => card.dataset.addonNone !== "true")
          .filter((card) => !card.closest("[data-native-mattresses-for]")?.hidden)
          .filter((card) => !card.closest("[data-native-accessories-for]")?.hidden)
          .filter((card) => !card.closest("[data-native-addon-group-for]")?.hidden)
          .filter((card) => card.querySelector("[data-addon-select]")?.checked)
          .map((card) => {
            const selector = card.querySelector("[data-addon-variant]");
            const checkbox = card.querySelector("[data-addon-select]");
            return {
              id: Number(selector?.value || checkbox.value),
              quantity: this.quantityFor(card),
              properties: {
                _addon_group_id: groupId,
                _parent_product_id: String(this.dataset.productId),
                _parent_variant_id: this.selectedVariantId,
                _addon_category: card.dataset.addonCategory
              }
            };
          });
      }

      selectedVariantMetadata() {
        const availability = this.querySelector(`[data-native-availability-for="${CSS.escape(this.selectedVariantId)}"]`);
        const noDrawersSelected = this.querySelector(
          '[data-native-addon-group-for]:not([hidden]) [data-addon-card][data-addon-category="divan_drawer"][data-addon-none="true"] [data-addon-select]:checked'
        );
        return {
          leadDays: availability?.dataset.leadDays || "",
          note: availability?.innerText?.trim() || "",
          status: availability?.dataset.availabilityStatus || "",
          drawerSelection: noDrawersSelected ? "No Drawers" : ""
        };
      }
    }
  );
}
